/**
 * CORE-1190 (остаток QA 02.09.2026) — распознавание returnTo комнаты для входа.
 *
 * `LoginScreen.navigateInternalReturnTo` возвращает вошедшего на его `returnTo`
 * SPA-переходом (`navigation.reset`) для набора известных путей и лишь для
 * незнакомых падает в запасной `window.location.href` — полную перезагрузку.
 * Путь Live-комнаты `/practice-rooms/session/<код>` в этот набор не входил,
 * поэтому возврат в комнату всегда шёл перезагрузкой. А `LiveRoomScreen`, из
 * которого гость ушёл на вход, остаётся смонтированным под экраном входа
 * (стек, не размонтирование), и его `beforeunload`-предохранитель на
 * перезагрузку поднимал системное окно «Закрыть сайт?». Отсюда лишний ручной
 * выбор перед возвратом в комнату.
 *
 * Логика вынесена сюда одной чистой функцией, чтобы её можно было пиньить без
 * поднятия всего экрана входа. Совпадает с linking-конфигом App.tsx
 * (`LiveRoomSession: 'practice-rooms/session/:roomId'`).
 */

/**
 * Код сессии из пути возврата в Live-комнату, либо `null`, если путь не про
 * комнату (тогда возврат идёт прежней веткой). Query и hash отбрасываются:
 * маршрут комнаты параметризован только кодом.
 */
export function matchLiveRoomSessionReturnTo(path: string): string | null {
    const pathname = path.split('#')[0].split('?')[0];
    const match = pathname.match(/^\/practice-rooms\/session\/([^/]+)$/);
    return match ? match[1] : null;
}

/** Экраны, которые держат ОДНУ комнату. Двух одновременно быть не может. */
const ROOM_SCREENS = ['LiveRoomSession', 'LiveSessionEntry', 'RoomAnnouncement'] as const;
/** Экраны входа — после успешного входа им в стеке делать нечего. */
const AUTH_SCREENS = ['Login', 'Register'] as const;

/**
 * CORE-1341 (сценарий 2) — стек, в который возвращают вошедшего из комнаты.
 *
 * ## Что было
 *
 * Возврат делал `navigation.replace(screen, …)`. `replace` заменяет ТЕКУЩИЙ
 * маршрут, то есть экран входа, — а комната, из которой гость на этот вход
 * ушёл, лежит СЛОЙ НИЖЕ и остаётся смонтированной. После входа в стеке
 * оказывались два экрана комнаты сразу.
 *
 * Это не теория: замер на dev 12.09.2026, гость в живой практике нажал
 * «Войти» и вернулся — в документе стало ДВА элемента `<video>` на один и тот
 * же файл, и `POST /watch-parties/{code}/join` дважды ответил 409 «You are
 * already in this party». В одном прогоне оба плеера доиграли, в другом оба
 * встали намертво: время замерло, у одного `readyState` упал с 4 до 1.
 *
 * Почему именно так — уже записано в CORE-521 про ленту: два `<video>` за один
 * URL душат друг друга, и та загрузка, которая должна играть, отменяется. На
 * быстром канале выживают обе, на узком — ни одной. Отсюда и рассказ из
 * задачи: у того, кто вошёл из практики, воспроизведение встало, а у соседа,
 * вошедшего сразу авторизованным (один экран), продолжалось. Перезагрузка
 * лечила, потому что новый документ поднимает ровно один экран.
 *
 * ## Что теперь
 *
 * Стек собирается так, чтобы экран комнаты остался РОВНО ОДИН. Выбрасываются
 * все прежние комнатные экраны и экраны входа, свежий кладётся сверху.
 *
 * История при этом не сносится: каталог, лента и прочее, по чему человек шёл
 * до комнаты, остаются под ней — поэтому не `reset` на единственный маршрут,
 * как в соседних ветках. Уход «назад» из комнаты ведёт туда же, куда и до
 * входа; случай пустой истории у комнаты свой и разобран в CORE-747.
 */
export function buildRoomReturnRoutes<T extends { name: string; params?: object }>(
    current: readonly T[],
    screen: string,
    params: object,
): { name: string; params?: object }[] {
    const kept = current.filter(
        (r) => !(ROOM_SCREENS as readonly string[]).includes(r.name)
            && !(AUTH_SCREENS as readonly string[]).includes(r.name),
    );
    return [...kept.map((r) => ({ name: r.name, params: r.params })), { name: screen, params }];
}

/**
 * CORE-1959 — ключи экранов комнаты, которые возврат после входа сменит свежим
 * экраном ТОЙ ЖЕ комнаты (то же имя экрана и тот же `roomId`). Их уход из хаба —
 * не уход человека: присутствие держит преемник (см. `navigation/roomHandoff`).
 * Экран другой комнаты сюда не попадает — оттуда человек действительно уходит.
 */
export function handedOffRoomRouteKeys(
    current: readonly { name: string; key?: string; params?: object }[],
    screen: string,
    params: { roomId: string },
): string[] {
    return current
        .filter((r) => r.name === screen
            && !!r.key
            && String((r.params as { roomId?: unknown } | undefined)?.roomId ?? '') === params.roomId)
        .map((r) => r.key as string);
}
